import { useState } from "react";
import { Mail, MapPin, Globe, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { CONTACT } from "@/data/rapro";

const INQUIRY_TYPES = [
  { value: "bpo", label: "BPO / Volume Hiring" },
  { value: "tech", label: "Technology Staffing" },
  { value: "contract", label: "Contract & Payroll" },
  { value: "general", label: "General Enquiry" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    role: "",
    inquiry_type: "bpo",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await submitContact(form);
      setSubmitted(true);
      toast.success(res.message || "Thanks — we'll be in touch.");
      setForm({ name: "", email: "", company: "", role: "", inquiry_type: "bpo", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-16 md:pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="label-eyebrow text-slate-500" data-testid="contact-eyebrow">
              <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
              Contact
            </div>
            <h1 data-testid="contact-headline" className="font-display mt-6 text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]">
              Let's build the <span className="text-[#00E676]">future</span> of your team.
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pl-8">
            <p className="text-slate-600 text-lg leading-relaxed">
              Share a few details about the role(s) you're hiring for. We'll respond within one business day with a tailored sourcing plan.
            </p>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* LEFT — Details */}
          <aside className="lg:col-span-5">
            <div className="bg-[#00E676] text-slate-900 p-8 md:p-10 border-2 border-slate-900 hard-shadow">
              <div className="label-eyebrow text-slate-900/70">
                <span className="inline-block h-[2px] w-6 bg-slate-900 align-middle mr-3" />
                Reach Us Directly
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-extrabold mt-4">
                Connect with a Hiring Professional
              </h2>
              <ul className="mt-8 space-y-6">
                <li className="flex items-start gap-4" data-testid="contact-email">
                  <Mail className="h-8 w-8 text-slate-900 shrink-0 mt-1" strokeWidth={2.25} />
                  <div>
                    <div className="label-eyebrow text-slate-900/70">Email</div>
                    <a href={`mailto:${CONTACT.email}`} className="font-display text-base font-bold mt-1 block hover:underline transition-colors break-all">{CONTACT.email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-address">
                  <MapPin className="h-8 w-8 text-slate-900 shrink-0 mt-1" strokeWidth={2.25} />
                  <div>
                    <div className="label-eyebrow text-slate-900/70">Office</div>
                    <p className="font-body text-sm mt-1 leading-relaxed text-slate-900/90">{CONTACT.address}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-website">
                  <Globe className="h-8 w-8 text-slate-900 shrink-0 mt-1" strokeWidth={2.25} />
                  <div>
                    <div className="label-eyebrow text-slate-900/70">Web</div>
                    <a
                      href="https://www.raprosolutions.com"
                      target="_blank"
                      rel="noreferrer"
                      className="font-display text-base font-bold mt-1 block hover:underline transition-colors"
                    >
                      {CONTACT.website}
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </aside>

          {/* RIGHT — Form */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div data-testid="contact-success" className="border-2 border-slate-900 p-10 hard-shadow">
                <div className="label-eyebrow text-[#00C853]">
                  <span className="inline-block h-[2px] w-6 bg-[#00E676] align-middle mr-3" />
                  Message Received
                </div>
                <h3 className="font-display text-3xl font-extrabold mt-3 text-slate-900">
                  Thanks for reaching out.
                </h3>
                <p className="mt-4 text-slate-600 text-lg">
                  A senior recruiter will respond to your enquiry within one business day. In the meantime, feel free to explore our services and supported technologies.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="send-another-button"
                  className="mt-8 inline-flex items-center gap-2 bg-[#00E676] text-slate-900 px-6 py-3 font-semibold hover:bg-[#00C853] transition-colors"
                >
                  Send Another Enquiry
                  <ArrowUpRight className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="border-2 border-slate-900 p-8 md:p-10 hard-shadow bg-white"
                noValidate
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <div>
                    <label htmlFor="name" className="label-eyebrow text-slate-500">Full Name *</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jane Smith"
                      className="rapro-input"
                      data-testid="contact-input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="label-eyebrow text-slate-500">Work Email *</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@company.com"
                      className="rapro-input"
                      data-testid="contact-input-email"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="label-eyebrow text-slate-500">Company</label>
                    <input
                      id="company"
                      name="company"
                      type="text"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Acme Inc."
                      className="rapro-input"
                      data-testid="contact-input-company"
                    />
                  </div>
                  <div>
                    <label htmlFor="role" className="label-eyebrow text-slate-500">Your Role</label>
                    <input
                      id="role"
                      name="role"
                      type="text"
                      value={form.role}
                      onChange={handleChange}
                      placeholder="Head of Talent"
                      className="rapro-input"
                      data-testid="contact-input-role"
                    />
                  </div>
                </div>

                <div className="mt-8">
                  <span className="label-eyebrow text-slate-500">What can we help with?</span>
                  <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" data-testid="contact-inquiry-group">
                    {INQUIRY_TYPES.map((t) => {
                      const active = form.inquiry_type === t.value;
                      return (
                        <button
                          type="button"
                          key={t.value}
                          data-testid={`inquiry-type-${t.value}`}
                          onClick={() => setForm((p) => ({ ...p, inquiry_type: t.value }))}
                          className={`px-4 py-2 text-sm font-medium border-2 transition-colors ${
                            active
                              ? "bg-[#00E676] text-slate-900 border-slate-900"
                              : "bg-white text-slate-900 border-slate-200 hover:border-slate-900"
                          }`}
                        >
                          {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8">
                  <label htmlFor="message" className="label-eyebrow text-slate-500">Tell us about the roles *</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Number of openings, skills, location preference, timeline..."
                    className="rapro-input resize-none"
                    data-testid="contact-input-message"
                  />
                </div>

                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-slate-500 max-w-sm">
                    By submitting, you agree to be contacted by RAPRO Solutions about your enquiry.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    className="group inline-flex items-center gap-2 bg-[#00E676] text-slate-900 px-7 py-3.5 font-semibold hover:bg-[#00C853] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Enquiry
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
